import * as SignatureCanvasModule from "react-signature-canvas";
import { Question } from "survey-core";
import { useEffect, useRef } from "react";
import { theme } from "antd";
import { Button } from "../components/Button";

// ponytail: react-signature-canvas is a UMD/CJS package. Rolldown's
// "consistent CJS interop" (Vite 8) double-wraps it as
// `{ default: { default: Component } }` in this ESM package, while other
// bundlers only wrap it once. Unwrap `.default` until we hit the actual
// component so this works regardless of bundler/interop mode (see the Sentry
// issue on the dealer intake widget crashing with "Element type is invalid").
function unwrapDefault<T>(mod: unknown): T {
  let value = mod;
  while (value && typeof value === "object" && "default" in value) {
    value = (value as { default: unknown }).default;
  }
  return value as T;
}

type SignatureCanvasComponent = typeof SignatureCanvasModule.default;
type SignatureCanvasInstance = InstanceType<SignatureCanvasComponent>;

const SignatureCanvas = unwrapDefault<SignatureCanvasComponent>(
  SignatureCanvasModule,
);

export class SignatureQuestionModel extends Question {
  getType() {
    return "signature";
  }
}

interface SignatureQuestionProps {
  question: SignatureQuestionModel;
}

export const SignatureQuestion = ({ question }: SignatureQuestionProps) => {
  const { token } = theme.useToken();
  const wrapRef = useRef<HTMLDivElement>(null);
  const sigRef = useRef<SignatureCanvasInstance>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const sig = sigRef.current;
    if (!wrap || !sig) return;

    const initialValue = question.value;
    let initialApplied = false;

    const resize = () => {
      const canvas = sig.getCanvas();
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      const isInitialCall = !initialApplied;
      initialApplied = true;

      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      const ctx = canvas.getContext("2d");
      ctx?.setTransform(ratio, 0, 0, ratio, 0, 0);

      if (isInitialCall && initialValue) {
        sig.fromDataURL(initialValue, {
          ratio,
          width: rect.width,
          height: rect.height,
        });
        return;
      }

      sig.clear();
      const isReadOnly = question.isReadOnlyAttr || question.isDisabledAttr;
      if (!isInitialCall && !isReadOnly && question.value !== undefined) {
        question.value = undefined;
      }
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [question.name]);

  const handleEnd = async () => {
    const canvas = sigRef.current?.getTrimmedCanvas();
    if (!canvas) return;

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b: Blob | null) => resolve(b), "image/png"),
    );

    if (blob) {
      const reader = new FileReader();
      question.value = await new Promise<string | undefined>(
        (resolve, reject) => {
          reader.onload = () =>
            resolve(
              typeof reader.result === "string" ? reader.result : undefined,
            );
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        },
      );
    } else {
      question.value = undefined;
    }
  };

  const clear = () => {
    const sig = sigRef.current;
    if (sig) {
      const canvas = sig.getCanvas();
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      // Reassigning width/height resets the bitmap and any transform.
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      canvas.getContext("2d")?.scale(ratio, ratio);
      sig.clear();
    }
    question.value = undefined;
  };

  const isDisabled = question.isReadOnlyAttr || question.isDisabledAttr;

  return (
    <div>
      <div ref={wrapRef} style={{ width: "100%" }}>
        <SignatureCanvas
          ref={sigRef}
          penColor="#111827"
          backgroundColor="#ffffff"
          onEnd={handleEnd}
          clearOnResize={false}
          canvasProps={{
            style: {
              width: "100%",
              height: 300,
              borderRadius: 8,
              border: `1px solid ${token.colorBorder}`,
              touchAction: "none",
              pointerEvents: isDisabled ? "none" : "auto",
            },
          }}
        />
      </div>
      {!isDisabled && (
        <Button type="default" onClick={clear} style={{ marginTop: 12 }}>
          Handtekening wissen
        </Button>
      )}
    </div>
  );
};
